import { AttributionService, LanguageService, logger } from '@web_opxp/features';
import { createBrowserHistory } from 'history';
import { sessionStorage } from 'web_core_library';
import { propagateExperiment } from '../components/trustpilotBadge/utils';
import ENV from './env';

export const history = createBrowserHistory({
  basename: ENV.BasePath,
});

export function addListener(listener: (path: string) => void): () => void {
  const currentPath = history.createHref(history.location);
  listener(currentPath);
  return history.listen((location) => {
    const path = history.createHref(location);
    listener(path);
  });
}

export const getNormalizedUrl = (url: URL) => {
  let normalizedUrl = url.toString();
  if (normalizedUrl.indexOf('/?') < 0 && normalizedUrl.indexOf('?') >= 0) {
    normalizedUrl = normalizedUrl.replace('?', '/?');
  }
  return normalizedUrl;
};

export const getExternalUrl = (url: string, lang?: string, session?: string) => {
  // get current origin
  const base = window.location.origin;
  // base will be applied only if url is relative
  const resultUrl = new URL(url, base);

  if (lang) {
    resultUrl.searchParams.set('lang', lang);
  }
  if (session) {
    resultUrl.searchParams.set('session', session);
  }
  AttributionService.updateSearchParams(resultUrl.searchParams);
  return getNormalizedUrl(resultUrl);
};

export const getInternalUrl = (path?: string, search?: string) => {
  return history.createHref({ pathname: path, search });
};

export const getBackUrl = (path?: string) => {
  const backUrlPath = path ?? history.location.pathname;
  const hasSearchParams = backUrlPath.includes('?');
  // parse current search string
  const searchParams = new URLSearchParams(hasSearchParams ? backUrlPath.split('?')[1] : history.location.search);
  // remove auth params but keep others
  searchParams.delete('signin');
  searchParams.delete('session');
  // do not remove experiment from back url (see BW-5128)
  // removeExperiment(searchParams);
  const backUrlSearch = searchParams.toString();
  const isExternal = backUrlPath.includes('http');
  let backUrl: URL;
  if (!isExternal) {
    const internalHref = getInternalUrl(backUrlPath, backUrlSearch);
    backUrl = new URL(internalHref, window.location.origin);
  } else {
    backUrl = new URL(backUrlPath);
    backUrl.search = backUrlSearch;
  }
  return getNormalizedUrl(backUrl);
};

export const getSsoRedirectUrl = (
  language?: string,
  signup = false,
  backUrlPath?: string,
  logout?: boolean,
  signin?: string
) => {
  let redirectUrl;
  if (logout) {
    redirectUrl = new URL(ENV.LogoutUrl);
  } else if (!signup) {
    redirectUrl = new URL(ENV.LoginUrl);
  } else {
    redirectUrl = new URL(ENV.RegistrationUrl);
  }
  const backUrl = getBackUrl(backUrlPath);
  redirectUrl.searchParams.set('backUrl', backUrl);
  if (language) {
    redirectUrl.searchParams.set('lang', language);
  }

  if (logout) {
    redirectUrl.searchParams.set('logout', '1');
  } else if (signin) {
    redirectUrl.searchParams.set('signin', signin);
  }
  // add attribution tracking parameters in internal format for propagation
  AttributionService.updateSearchParams(redirectUrl.searchParams);
  return getNormalizedUrl(redirectUrl);
};

export function getRegistrationUrl(backUrl: string, lang?: string) {
  return getSsoRedirectUrl(lang, true, backUrl);
}

export const redirectToSSO = (backUrl?: string, logout?: boolean, signin?: string, signup = false) => {
  const language = LanguageService.getLanguage() ?? 'de';
  const redirectUrl = getSsoRedirectUrl(language, signup, backUrl, logout, signin);
  sessionStorage.remove('session');
  return window.location.assign(redirectUrl);
};

export function redirectLogout() {
  // redirect to onboarding page after logout
  return redirectToSSO(ENV.OnboardingAppPath, true);
}

export function exchangeAccessToken(accessToken?: string) {
  return redirectToSSO(undefined, false, accessToken);
}

export function redirectLogin(backUrlPath = '') {
  return redirectToSSO(backUrlPath, false);
}

export const getCurrentPath = () => history.location.pathname;

export function redirectSignup(backUrlPath?: string) {
  return redirectToSSO(backUrlPath, false, '', true);
}

export function addBasicSearchParams(url: URL, session?: string, trackingParams = false): void {
  const currentUrl = new URL(window && window.location.href);
  // remove tokens from current url, session token will be replaced with fresh one if needed
  currentUrl.searchParams.delete('signin');
  currentUrl.searchParams.delete('session');

  if (trackingParams) {
    // forward any tracking parameters
    AttributionService.updateSearchParams(currentUrl.searchParams);
  }

  // set base parameters
  url.searchParams.set('backUrl', getBackUrl());
  url.searchParams.set('forwardSession', '1');

  // add session token if user is authorized
  if (session) {
    url.searchParams.set('session', session);
  }
}

export const externalRedirect = (url: string, lang?: string, session?: string) => {
  if (!url) {
    return;
  }
  const redirectUrl = getExternalUrl(url, lang, session);
  return window.location.assign(redirectUrl);
};

export const getTrainingUrl = (subpage = '', language?: string, session?: string) => {
  return getExternalUrl(`${ENV.TrainingAppPath}${subpage}`, language, session);
};

export const getOnboardingUrl = (language?: string, session?: string) =>
  getExternalUrl(ENV.OnboardingAppPath, language, session);

export const getUserPageUrl = (language?: string, session?: string) =>
  getExternalUrl(ENV.UserAppPath, language, session);

export const getZendeskUrl = (lang: string, session?: string) => {
  const locale = lang === 'en' ? 'en-Us' : lang;
  const tokenParam = session ? `?signin=${session}` : '';
  return `${ENV.ApiUrl}/zendesk/${locale}${tokenParam}`;
};

// we can implement here language and/or session propagation if this will be needed in the future
export const getBlogUrl = (lang: string) => `${ENV.BlogPath}/${lang}`;

export const getEffectivenessUrl = (lang: string) => {
  const blogUrl = getBlogUrl(lang);
  let path = '';
  switch (lang) {
    case 'en':
      path = '/bulletproof-brain-training-study-proves-effectiveness-of-neuronation-premium/';
      break;
    case 'de':
      path = '/neue-studie-beweist-neuronation-mit-hoher-signifikanz-effektiv/';
      break;
    case 'fr':
      path = '/resultats-detude-neuronation-ameliore-la-memoire/';
      break;
    case 'ru':
      path =
        '/%d1%80%d0%b5%d0%b7%d1%83%d0%bb%d1%8c%d1%82%d0%b0%d1%82%d1%8b-%d0%b8%d1%81%d1%81%d0%bb%d0%b5%d0%b4%d0%be%d0%b2%d0%b0%d0%bd%d0%b8%d1%8f-neuronation-%d1%83%d0%bb%d1%83%d1%87%d1%88%d0%b0%d0%b5%d1%82/';
      break;
  }
  const url = new URL(path, blogUrl);
  AttributionService.updateSearchParams(url.searchParams);
  return url.toString();
};

export const getPrivacyPolicyUrl = (lang: string) => {
  const url = new URL(ENV.PrivacyPolicyUrl);
  url.searchParams.set('lang', lang);
  AttributionService.updateSearchParams(url.searchParams);
  return url.toString();
};

export type Location = typeof history.location;

export const getCurrentSearchParams = (location?: Location) => {
  const search = location ? location.search : history.location.search;
  return new URLSearchParams(search);
};

export const updateSearchParams = (newSearch: URLSearchParams) => {
  const location = history.location;
  const newSearchQuery = `?${newSearch.toString()}`;
  if (location.search !== newSearchQuery) {
    location.search = newSearchQuery;
    history.replace(location);
  }
};

const getSocialUrl = (subpage = '', language?: string, session?: string) =>
  getExternalUrl(`${ENV.SocialAppPath}${subpage}`, language, session);

export const getFriendsUrl = (language?: string, session?: string) => getSocialUrl('/friends', language, session);

export const getMessagesUrl = (language?: string, session?: string) => getSocialUrl('/messages', language, session);

export const getMigrationUrl = (lang: string, session?: string) =>
  getExternalUrl(`${ENV.UserAppPath}/switch-back`, lang, session);

export const redirectMigration = (lang: string, session?: string) =>
  externalRedirect(`${ENV.UserAppPath}/switch-back`, lang, session);

const getCheckoutSearchParams = (productId: number, lang: string, country?: string, coupon?: string) => {
  const searchParams = getCurrentSearchParams();
  searchParams.set('productId', `${productId}`);
  searchParams.set('lang', lang);
  if (country) {
    searchParams.set('country', country);
  }
  if (coupon) {
    searchParams.set('coupon', coupon);
  } else {
    // we need to remove coupon if its present in the url but not applicable to given product
    searchParams.delete('coupon');
  }
  return searchParams.toString();
};

const getCheckoutUrl = (planId: string, productId: number, lang: string, country?: string, coupon?: string) => {
  const search = getCheckoutSearchParams(productId, lang, country, coupon);
  return getInternalUrl(`/checkout/${planId}`, search);
};

export const getCheckoutBackUrl = (
  planId: string,
  productId: number,
  lang: string,
  country?: string,
  coupon?: string
) => {
  const internalHref = getCheckoutUrl(planId, productId, lang, country, coupon);
  const backUrl = new URL(internalHref, window.location.origin);
  return getNormalizedUrl(backUrl);
};

export const redirectInternal = (url: string, state = {}, search?: string) => {
  return history.push({ pathname: url, state, search });
};

export const redirectToCheckout = (
  planId: string,
  productId: number,
  lang: string,
  country?: string,
  coupon?: string
) => {
  const search = getCheckoutSearchParams(productId, lang, country, coupon);
  const internalHref = `/checkout/${planId}/`;
  redirectInternal(internalHref, undefined, search);
};

export const redirectToManage = () => {
  const internalHref = '/manage/';
  redirectInternal(internalHref);
};

export const redirectToCheckoutSuccess = () => {
  // BW-6866: preserve existing search parameters to keep ob_flow if it is set
  const searchParams = getCurrentSearchParams();
  const search = searchParams.toString();
  const internalHref = `/checkout/success/`;
  redirectInternal(internalHref, undefined, search);
};

export const redirectToCheckoutCancel = () => {
  // BW-6866: preserve existing search parameters to keep ob_flow if it is set
  const searchParams = getCurrentSearchParams();
  const search = searchParams.toString();
  const internalHref = `/checkout/cancel/`;
  redirectInternal(internalHref, undefined, search);
};

export const redirectToTraining = (language?: string, session?: string) => {
  const trainingUrl = getTrainingUrl('', language, session);
  return window.location.assign(trainingUrl);
};

export const redirectBackToSubscribe = (cb?: boolean) => {
  // BW-6866: preserve existing search parameters to keep ob_flow if it is set
  const searchParams = getCurrentSearchParams();
  const search = searchParams.toString();
  const path = cb ? '/cb/' : '/';
  const internalHref = `${path}`;
  redirectInternal(internalHref, undefined, search);
};

export const getProductIdFromUrl = () => {
  const searchParams = getCurrentSearchParams();
  const productId = searchParams.get('productId');
  if (!productId) {
    return;
  }
  return productId;
};

export const getCountryCodeFromUrl = () => {
  const searchParams = getCurrentSearchParams();
  const country = searchParams.get('country');
  if (!country) {
    return;
  }
  return country;
};

export const refreshToken = () => {
  sessionStorage.remove('session');
  const backUrlPath = getCurrentPath();
  return redirectLogin(backUrlPath);
};

export function buildLegacyCheckoutUrl(
  productId: number,
  coupon: string | null,
  lang: string,
  session?: string
): string {
  const checkoutUrl = new URL(ENV.CheckoutUrl);

  // set selected productId
  checkoutUrl.searchParams.set('productId', productId.toString());

  // add other default params
  addBasicSearchParams(checkoutUrl, session, true);

  // set training url to go after complete
  checkoutUrl.searchParams.set('completeUrl', getTrainingUrl('', lang));

  // set coupon information
  if (coupon) {
    checkoutUrl.searchParams.set('coupon', coupon);
  } else {
    checkoutUrl.searchParams.set('noCoupon', '1');
  }

  // propagate experiment parameters to checkout
  propagateExperiment(checkoutUrl.searchParams);

  // set language
  checkoutUrl.searchParams.set('lang', lang);

  return checkoutUrl.toString();
}

export const getJobsUrl = () => {
  const jobsUrl = new URL('https://neuronation.breezy.hr/');
  return jobsUrl.toString();
};

export const getGroupPackageUrl = () => {
  const groupPackageUrl = getExternalUrl(`${ENV.OnboardingAppPath}/lp/corp`);
  return groupPackageUrl.toString();
};

let windowObjectReference: Window | null = null;
let previousURL: string;

export function openExternalUrl(url: string) {
  if (windowObjectReference === null || windowObjectReference.closed) {
    windowObjectReference = window.open(url, 'NNChildWindow');
  } else if (previousURL !== url) {
    windowObjectReference = window.open(url, 'NNChildWindow');
    if (!windowObjectReference) {
      return;
    }
    windowObjectReference.focus();
  } else {
    windowObjectReference.focus();
  }
  previousURL = url;
}

export function closeExternalUrlTab() {
  if (windowObjectReference === null || windowObjectReference.closed) {
    logger('No external tab is opened!');
  } else {
    logger('Trying to close external tab...');
    windowObjectReference.close();
  }
}

export function isCustomObFlowActive(location: Location) {
  const searchParams = getCurrentSearchParams(location);
  const obValueStr = searchParams.get('ob_flow');
  return obValueStr !== null;
}

export function isSubscribe(location: Location) {
  const path = location.pathname;
  const isSubscribe = path === '/' || /subscribe|cb|checkout/i.test(path);
  return isSubscribe;
}
